import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Box, Icon, Stack, Typography } from '@liscio/ui';

import { ContactEmailLookupTextField } from './ContactEmailLookupTextField';
import { ContactSuggestionPanel } from './ContactSuggestionPanel';
import { ContactSuggestion } from './contactSuggestions';
import { CreateContactFormValues } from './createContactValidationSchema';
import { CreateContactFormLayout } from './types';
import { LabeledField } from 'components/CreateAccountForm/CreateAccountFormFieldPrimitives';
import { useContactSuggestionsByEmail } from 'components/CreateContactForm/useContactSuggestionsByEmail';
import { PhoneInput } from 'components/reactHookFormFields';

export type { CreateContactFormLayout } from './types';

export type CreateContactFormVariant = 'default' | 'emailFirst';

export interface CreateContactFormFieldsProps {
  /**
   * `responsive` — First/last name and job/phone sit side-by-side from `sm` up.
   * `single-column` — all fields full width.
   */
  layout?: CreateContactFormLayout;
  /** When false, primary email is optional (unaffiliated contact path). */
  emailRequired?: boolean;
  /** `emailFirst` — onboarding flow: email only until a match action reveals the form. */
  variant?: CreateContactFormVariant;
  /**
   * `default` variant only: show the email-based existing-contact / third-party
   * lookup beneath the (always-visible) email field. Onboarding's `emailFirst`
   * variant has its own lookup and ignores this.
   */
  enableSuggestions?: boolean;
  /** Hide name/phone fields until Gmail prefill or manual create (inline create panel). */
  hideDetailsUntilRevealed?: boolean;
  primaryEmailLabel?: string;
  onSelectSuggestion?: (suggestion: ContactSuggestion) => void;
  onSelectExistingContact?: (suggestion: ContactSuggestion) => void;
  onFormRevealChange?: (isRevealed: boolean) => void;
  onCreateManually?: () => void;
  /** When true, the full contact form is shown (controlled by parent in email-first mode). */
  isFormRevealed?: boolean;
  showInviteContactCheckbox?: boolean;
}

const ContactDetailsFields: React.FC<{
  layout: CreateContactFormLayout;
  emailRequired: boolean;
  enableSuggestions?: boolean;
  hideDetailsUntilRevealed?: boolean;
  primaryEmailLabel?: string;
  onSelectSuggestion?: (suggestion: ContactSuggestion) => void;
  onSelectExistingContact?: (suggestion: ContactSuggestion) => void;
  onFormRevealChange?: (isRevealed: boolean) => void;
  isFormRevealed?: boolean;
  showInviteContactCheckbox?: boolean;
}> = ({
  layout,
  emailRequired,
  enableSuggestions = false,
  hideDetailsUntilRevealed = false,
  primaryEmailLabel = 'Primary Email, used to send the portal invite',
  onSelectSuggestion,
  onSelectExistingContact,
  onFormRevealChange,
  isFormRevealed: isFormRevealedProp,
  showInviteContactCheckbox = false,
}) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CreateContactFormValues>();

  const pairedFieldBreakpoint = layout === 'single-column' ? 12 : 6;

  const primaryEmailValue = useWatch({ control, name: 'primary_email' });
  const { suggestions, isLoading } = useContactSuggestionsByEmail(
    enableSuggestions ? primaryEmailValue ?? '' : ''
  );
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [internalDetailsRevealed, setInternalDetailsRevealed] = useState(
    !hideDetailsUntilRevealed
  );
  const detailsRevealed = isFormRevealedProp ?? internalDetailsRevealed;

  const setDetailsRevealed = useCallback(
    (revealed: boolean) => {
      if (isFormRevealedProp === undefined) {
        setInternalDetailsRevealed(revealed);
      }
      onFormRevealChange?.(revealed);
    },
    [isFormRevealedProp, onFormRevealChange]
  );

  useEffect(() => {
    if (isFormRevealedProp !== undefined) {
      setInternalDetailsRevealed(isFormRevealedProp);
    }
  }, [isFormRevealedProp]);

  const applySuggestion = useCallback(
    (suggestion: ContactSuggestion) => {
      setValue('primary_email', suggestion.primary_email, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('first_name', suggestion.first_name ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('last_name', suggestion.last_name ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('phone', suggestion.phone_number ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      if (suggestion.job_title) {
        setValue('job_title', suggestion.job_title, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      if (hideDetailsUntilRevealed) {
        setDetailsRevealed(true);
      }
      onSelectSuggestion?.(suggestion);
      setIsEmailFocused(false);
    },
    [hideDetailsUntilRevealed, onSelectSuggestion, setDetailsRevealed, setValue]
  );

  const handleSelectExisting = useCallback(
    (suggestion: ContactSuggestion) => {
      if (onSelectExistingContact) {
        onSelectExistingContact(suggestion);
        setIsEmailFocused(false);
        return;
      }
      applySuggestion(suggestion);
    },
    [applySuggestion, onSelectExistingContact]
  );

  const handleCreateManually = useCallback(() => {
    if (hideDetailsUntilRevealed) {
      setDetailsRevealed(true);
    }
    setIsEmailFocused(false);
  }, [hideDetailsUntilRevealed, setDetailsRevealed]);

  const showSuggestions =
    enableSuggestions &&
    (isEmailFocused || (hideDetailsUntilRevealed && !detailsRevealed)) &&
    (primaryEmailValue ?? '').trim().length > 0;

  // While the email field is still in lookup/search mode (details not yet
  // revealed), the user is searching for a contact — don't surface email
  // validation. Validation kicks in once the full form is shown.
  const inEmailSearchMode = hideDetailsUntilRevealed && !detailsRevealed;

  return (
    <>
      <Grid item xs={12}>
        <LabeledField label={primaryEmailLabel} required={emailRequired}>
          <Controller
            control={control}
            name="primary_email"
            render={({ field }) => (
              <ContactEmailLookupTextField
                ref={field.ref}
                value={field.value ?? ''}
                fullWidth
                placeholder="Search by name or email"
                error={!inEmailSearchMode && Boolean(errors.primary_email)}
                helperText={
                  inEmailSearchMode ? undefined : errors.primary_email?.message
                }
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={
                  enableSuggestions
                    ? () => {
                        field.onBlur();
                        window.setTimeout(() => setIsEmailFocused(false), 150);
                      }
                    : field.onBlur
                }
                onFocus={
                  enableSuggestions ? () => setIsEmailFocused(true) : undefined
                }
                InputProps={
                  enableSuggestions && isLoading
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress color="inherit" size={16} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            )}
          />
        </LabeledField>
        {showSuggestions && (
          <ContactSuggestionPanel
            emailQuery={primaryEmailValue ?? ''}
            suggestions={suggestions}
            isLoading={isLoading}
            onSelectExisting={handleSelectExisting}
            onSelectGmail={applySuggestion}
            onCreateManually={handleCreateManually}
          />
        )}
      </Grid>

      {detailsRevealed && (
        <>
          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="First Name" required>
              <TextField
                {...register('first_name')}
                fullWidth
                placeholder="Jane"
                error={Boolean(errors.first_name)}
                helperText={errors.first_name?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Last Name" required>
              <TextField
                {...register('last_name')}
                fullWidth
                placeholder="Doe"
                error={Boolean(errors.last_name)}
                helperText={errors.last_name?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Job Title">
              <TextField
                {...register('job_title')}
                fullWidth
                placeholder="CFO"
                error={Boolean(errors.job_title)}
                helperText={errors.job_title?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Mobile Phone">
              <PhoneInput control={control} name="phone" />
              {errors.phone?.message && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.phone.message}
                </Typography>
              )}
            </LabeledField>
          </Grid>

          {showInviteContactCheckbox && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('send_invitation')} />}
                label="Invite this contact to Liscio"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 4, mt: -0.5 }}
              >
                Send a portal invitation after the contact is created.
              </Typography>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

const EmailFirstContactFields: React.FC<{
  layout: CreateContactFormLayout;
  emailRequired: boolean;
  onSelectSuggestion?: (suggestion: ContactSuggestion) => void;
  onFormRevealChange?: (isRevealed: boolean) => void;
  onCreateManually?: () => void;
  isFormRevealed?: boolean;
  showInviteContactCheckbox?: boolean;
}> = ({
  layout,
  emailRequired,
  onSelectSuggestion,
  onFormRevealChange,
  onCreateManually,
  isFormRevealed: isFormRevealedProp,
  showInviteContactCheckbox = false,
}) => {
  const {
    register,
    control,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext<CreateContactFormValues>();
  const primaryEmailValue = useWatch({ control, name: 'primary_email' });
  const { suggestions, isLoading } = useContactSuggestionsByEmail(
    primaryEmailValue ?? ''
  );

  const [internalFormRevealed, setInternalFormRevealed] = useState(false);
  const [gmailPrefillActive, setGmailPrefillActive] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const emailContainerRef = useRef<HTMLDivElement>(null);

  const isFormRevealed = isFormRevealedProp ?? internalFormRevealed;
  const pairedFieldBreakpoint = layout === 'single-column' ? 12 : 6;

  const setFormRevealed = useCallback(
    (revealed: boolean) => {
      if (isFormRevealedProp === undefined) {
        setInternalFormRevealed(revealed);
      }
      onFormRevealChange?.(revealed);
    },
    [isFormRevealedProp, onFormRevealChange]
  );

  useEffect(() => {
    if (isFormRevealedProp !== undefined) {
      setInternalFormRevealed(isFormRevealedProp);
    }
  }, [isFormRevealedProp]);

  const resetDetailFields = useCallback(() => {
    resetField('first_name', { defaultValue: '' });
    resetField('last_name', { defaultValue: '' });
    resetField('job_title', { defaultValue: '' });
    resetField('phone', { defaultValue: '' });
    resetField('send_invitation', { defaultValue: false });
    setGmailPrefillActive(false);
  }, [resetField]);

  const clearRevealedFormFields = useCallback(() => {
    resetDetailFields();
    setFormRevealed(false);
  }, [resetDetailFields, setFormRevealed]);

  const applyGmailSuggestion = useCallback(
    (suggestion: ContactSuggestion) => {
      setValue('primary_email', suggestion.primary_email, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('first_name', suggestion.first_name ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('last_name', suggestion.last_name ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('phone', suggestion.phone_number ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      if (suggestion.job_title) {
        setValue('job_title', suggestion.job_title, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      setGmailPrefillActive(true);
      setFormRevealed(true);
      onSelectSuggestion?.(suggestion);
    },
    [onSelectSuggestion, setFormRevealed, setValue]
  );

  const handleSelectExisting = useCallback(
    (suggestion: ContactSuggestion) => {
      setValue('primary_email', suggestion.primary_email, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearRevealedFormFields();
      onSelectSuggestion?.(suggestion);
      setIsEmailFocused(false);
    },
    [clearRevealedFormFields, onSelectSuggestion, setValue]
  );

  const handleCreateManually = useCallback(() => {
    resetDetailFields();
    setFormRevealed(true);
    onCreateManually?.();
    setIsEmailFocused(false);
  }, [onCreateManually, resetDetailFields, setFormRevealed]);

  const showSuggestions =
    (isEmailFocused || !isFormRevealed) &&
    (primaryEmailValue ?? '').trim().length > 0;

  return (
    <>
      <Grid item xs={12}>
        <Box ref={emailContainerRef} width="100%">
          <LabeledField
            label={
              <>
                Client email{' '}
                <Box
                  component="span"
                  sx={{
                    px: 0.5,
                    py: 0.125,
                    borderRadius: 0.5,
                    bgcolor: (theme) => `${theme.palette.info.light}33`,
                    color: 'info.dark',
                    fontWeight: 700,
                  }}
                >
                  {showInviteContactCheckbox
                    ? 'Invitation optional'
                    : 'No invitation will be sent'}
                </Box>
              </>
            }
            required={emailRequired}
          >
            <Controller
              control={control}
              name="primary_email"
              render={({ field }) => (
                <ContactEmailLookupTextField
                  ref={field.ref}
                  value={field.value ?? ''}
                  fullWidth
                  placeholder="Search by name or email"
                  error={Boolean(errors.primary_email)}
                  onFocus={() => setIsEmailFocused(true)}
                  onBlur={() => {
                    field.onBlur();
                    window.setTimeout(() => setIsEmailFocused(false), 150);
                  }}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    if (isFormRevealed) {
                      clearRevealedFormFields();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon="emails" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {isLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : field.value ? (
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Clear email"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              field.onChange('');
                              clearRevealedFormFields();
                            }}
                          >
                            <Icon icon="close" sx={{ fontSize: 16 }} />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LabeledField>

          {!isFormRevealed && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              We&apos;ll check existing contacts, Gmail, and Outlook before you
              create anything.
            </Typography>
          )}

          {showSuggestions && (
            <ContactSuggestionPanel
              emailQuery={primaryEmailValue ?? ''}
              suggestions={suggestions}
              isLoading={isLoading}
              onSelectExisting={handleSelectExisting}
              onSelectGmail={applyGmailSuggestion}
              onCreateManually={handleCreateManually}
            />
          )}
        </Box>
      </Grid>

      {isFormRevealed && (
        <>
          {gmailPrefillActive && (
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Icon
                  icon="emails"
                  sx={{ color: 'text.secondary', fontSize: 16 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Name &amp; phone pulled from your email contact (Gmail or
                  Outlook). Edit anything before continuing.
                </Typography>
              </Stack>
            </Grid>
          )}

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="First Name" required>
              <TextField
                {...register('first_name')}
                fullWidth
                placeholder="Jordan"
                error={Boolean(errors.first_name)}
                helperText={errors.first_name?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Last Name" required>
              <TextField
                {...register('last_name')}
                fullWidth
                placeholder="Chen"
                error={Boolean(errors.last_name)}
                helperText={errors.last_name?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Job Title">
              <TextField
                {...register('job_title')}
                fullWidth
                placeholder="CFO"
                error={Boolean(errors.job_title)}
                helperText={errors.job_title?.message}
                autoComplete="off"
              />
            </LabeledField>
          </Grid>

          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Mobile Phone">
              <PhoneInput control={control} name="phone" />
              {errors.phone?.message && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.phone.message}
                </Typography>
              )}
            </LabeledField>
          </Grid>

          {showInviteContactCheckbox && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('send_invitation')} />}
                label="Invite this contact to Liscio"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 4, mt: -0.5 }}
              >
                Send a portal invitation after the contact is created.
              </Typography>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

/**
 * Contact creation fields (name, email, job title, phone).
 * Must be rendered inside a parent `FormProvider` — use `useCreateContactForm()`.
 */
export const CreateContactFormFields: React.FC<
  CreateContactFormFieldsProps
> = ({
  layout = 'responsive',
  emailRequired = true,
  variant = 'default',
  enableSuggestions = false,
  hideDetailsUntilRevealed = false,
  primaryEmailLabel,
  onSelectSuggestion,
  onSelectExistingContact,
  onFormRevealChange,
  isFormRevealed,
  onCreateManually,
  showInviteContactCheckbox,
}) => {
  if (variant === 'emailFirst') {
    return (
      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <EmailFirstContactFields
          layout={layout}
          emailRequired={emailRequired}
          onSelectSuggestion={onSelectSuggestion}
          onFormRevealChange={onFormRevealChange}
          onCreateManually={onCreateManually}
          isFormRevealed={isFormRevealed}
          showInviteContactCheckbox={showInviteContactCheckbox}
        />
      </Grid>
    );
  }

  return (
    <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
      <ContactDetailsFields
        layout={layout}
        emailRequired={emailRequired}
        enableSuggestions={enableSuggestions}
        hideDetailsUntilRevealed={hideDetailsUntilRevealed}
        primaryEmailLabel={primaryEmailLabel}
        onSelectSuggestion={onSelectSuggestion}
        onSelectExistingContact={onSelectExistingContact}
        onFormRevealChange={onFormRevealChange}
        isFormRevealed={isFormRevealed}
        showInviteContactCheckbox={showInviteContactCheckbox}
      />
    </Grid>
  );
};
